import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Clock, FileText, CheckCircle, ArrowRight } from 'lucide-react';
import { regulatoryServices as mockServices, filterRegulatoryServices } from '../data/mock';
import { contentAPI } from '../services/api';

const RegulatoryDetail = () => {
  const { slug } = useParams();
  const [services, setServices] = useState(mockServices);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    contentAPI.get('regulatory_services').then(res => {
      if (Array.isArray(res.data) && res.data.length) setServices(filterRegulatoryServices(res.data));
    }).catch(() => {}).finally(() => setLoading(false));
  }, [slug]);

  const service = useMemo(() => services.find(s => s.slug === slug), [services, slug]);
  const otherServices = useMemo(() => services.filter(s => s.id !== service?.id).slice(0, 3), [services, service]);

  if (loading) return <div className="min-h-screen pt-20 flex items-center justify-center"><p>Loading...</p></div>;

  if (!service) {
    return <Navigate to="/regulatory-consultancy" replace />;
  }

  return (
    <div className="min-h-screen pt-20">
      {/* Back Button */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link
            to="/regulatory-consultancy"
            className="inline-flex items-center text-gray-600 hover:text-blue-600 transition-colors duration-200"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Regulatory Services
          </Link>
        </div>
      </div>

      {/* Hero */}
      <section className="relative h-96 flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 to-gray-900/70"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{service.title}</h1>
          <p className="text-xl text-gray-200 max-w-2xl">{service.description}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-10">
              {/* Process Steps */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <CheckCircle className="w-7 h-7 text-blue-600" />
                  Process Steps
                </h2>
                <div className="space-y-4">
                  {service.process.map((step, index) => (
                    <div key={step} className="flex gap-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                        {index + 1}
                      </div>
                      <div className="flex-1 pt-1">
                        <p className="text-gray-700 leading-relaxed">{step}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Required Documents */}
              <div className="bg-gray-50 rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <FileText className="w-7 h-7 text-emerald-600" />
                  Required Documents
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {service.documents.map((doc) => (
                    <div key={doc} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-emerald-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700">{doc}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Benefits */}
              <div className="bg-gradient-to-br from-blue-50 to-emerald-50 rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Why Work with SOLARIAN?</h2>
                <p className="text-gray-700 text-lg leading-relaxed">{service.benefits}</p>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 sticky top-24">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Quick Info</h3>
                
                <div className="space-y-6">
                  <div>
                    <div className="flex items-center gap-2 text-gray-600 mb-2">
                      <Clock className="w-5 h-5 text-blue-600" />
                      <span className="font-medium">Timeline</span>
                    </div>
                    <p className="text-lg font-semibold text-gray-900 ml-7">{service.timeline}</p>
                  </div>

                  <div className="pt-6 border-t border-gray-200">
                    <h4 className="font-semibold text-gray-900 mb-3">What We Need From You</h4>
                    <ul className="space-y-2">
                      {service.documents.slice(0, 3).map((doc) => (
                        <li key={doc} className="text-sm text-gray-600 flex items-start gap-2">
                          <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-1.5 flex-shrink-0"></div>
                          <span>{doc}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="mt-8 pt-8 border-t border-gray-200">
                  <Link
                    to="/contact"
                    className="block w-full px-6 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 text-center transform hover:scale-105 transition-all duration-200 shadow-md"
                  >
                    Request Expert Support
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Other Regulatory Services</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {otherServices.map((s) => (
              <Link
                key={s.id}
                to={`/regulatory/${s.slug}`}
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{s.title}</h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">{s.description}</p>
                <div className="flex items-center text-blue-600 font-medium text-sm">
                  Learn More
                  <ArrowRight className="w-4 h-4 ml-1" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default RegulatoryDetail;
